(() => {
  if (window.__cpCollectionSwapInstalled) return;
  window.__cpCollectionSwapInstalled = true;

  const SECTION_ID = "product-card";
  const SWATCH_SELECTOR = "[data-cp-swatch-link][data-cp-product-handle]";

  const cache = new Map();
  const inFlight = new Map();

  const parseHTML = (html) => new DOMParser().parseFromString(html, "text/html");

  function hardStop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  function findSwatchFromEvent(e) {
    if (typeof e.composedPath === "function") {
      for (const n of e.composedPath()) {
        if (n?.nodeType === 1) {
          if (n.matches?.(SWATCH_SELECTOR)) return n;
          const hit = n.closest?.(SWATCH_SELECTOR);
          if (hit) return hit;
        }
      }
    }
    return e.target?.closest?.(SWATCH_SELECTOR) || null;
  }

  async function fetchSection(handle) {
    const key = `${SECTION_ID}:${handle}`;
    if (cache.has(key)) return cache.get(key);
    if (inFlight.has(key)) return inFlight.get(key);

    const p = fetch(`/products/${handle}?section_id=${encodeURIComponent(SECTION_ID)}`, {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Fetch failed (${r.status})`);
        return r.text();
      })
      .then((html) => {
        cache.set(key, html);
        inFlight.delete(key);
        return html;
      })
      .catch((err) => {
        inFlight.delete(key);
        throw err;
      });

    inFlight.set(key, p);
    return p;
  }

  function upgradeCustomElements(root) {
    root.querySelectorAll("*").forEach((el) => {
      if (el.tagName && el.tagName.includes("-")) {
        try { customElements.upgrade(el); } catch (e) {}
      }
    });
  }

  async function swap(handle, oldCardInner, clickedSwatch) {
    const html = await fetchSection(handle);
    const doc = parseHTML(html);

    const newCardInner = doc.querySelector("product-card");
    if (!newCardInner) throw new Error("No product-card returned from server.");

    const oldCardRoot = oldCardInner.closest("product-card-link") || oldCardInner;

    // 1) IDs / handle & Transition Animation Data
    const newId = newCardInner.getAttribute("data-product-id");
    oldCardInner.setAttribute("data-cp-handle", handle);
    oldCardInner.setAttribute("data-product-id", newId);
    
    if (oldCardRoot !== oldCardInner) {
      oldCardRoot.setAttribute("data-product-id", newId);
      
      // FIX: Update the hidden media URL so the zoom transition into the PDP uses the correct color
      const newCardRoot = doc.querySelector("product-card-link");
      if (newCardRoot) {
        if (newCardRoot.hasAttribute("data-featured-media-url")) {
          oldCardRoot.setAttribute("data-featured-media-url", newCardRoot.getAttribute("data-featured-media-url"));
        }
        if (newCardRoot.hasAttribute("data-cp-card-url")) {
          oldCardRoot.setAttribute("data-cp-card-url", newCardRoot.getAttribute("data-cp-card-url"));
        }
      }
    }

    // 2) Update main links
    const newMainLink = newCardInner.querySelector("a[href*='/products/']:not([data-cp-swatch-link])");
    if (newMainLink) {
      const newHref = newMainLink.getAttribute("href");
      oldCardRoot.querySelectorAll("a").forEach((link) => {
        if (
          !link.hasAttribute("data-cp-swatch-link") &&
          link.hasAttribute("href") &&
          link.getAttribute("href").includes("/products/")
        ) {
          link.setAttribute("href", newHref);
        }
      });
    }

    // 3) Title text injection
    const oldTitleSpan = oldCardInner.querySelector(".product-card__link .visually-hidden");
    const newTitleSpan = newCardInner.querySelector(".product-card__link .visually-hidden");
    if (oldTitleSpan && newTitleSpan) {
      const oldText = oldTitleSpan.textContent.trim();
      const newText = newTitleSpan.textContent.trim();
      if (oldText && newText && oldText !== newText) {
        const walker = document.createTreeWalker(oldCardInner, NodeFilter.SHOW_TEXT, null, false);
        let node;
        while ((node = walker.nextNode())) {
          if (node.nodeValue.includes(oldText)) node.nodeValue = node.nodeValue.replace(oldText, newText);
        }
        oldTitleSpan.textContent = newText;
      }
    }

    // 4) Price injection
    const oldPriceSpan = oldCardInner.querySelector(".price");
    const newPriceSpan = newCardInner.querySelector(".price");
    if (oldPriceSpan && newPriceSpan) {
      oldPriceSpan.innerHTML = newPriceSpan.innerHTML.replace(/\s*[A-Z]{3}/g, "");
    }

    // 5) Media swap (Entire Gallery Wrapper)
    const oldGallery = oldCardInner.querySelector(".card-gallery");
    const newGallery = newCardInner.querySelector(".card-gallery");
    
    if (oldGallery && newGallery) {
      const oldImgs = oldGallery.querySelectorAll("img");
      const newImgs = newGallery.querySelectorAll("img");
      const oldVideos = oldGallery.querySelectorAll("video");
      const newVideos = newGallery.querySelectorAll("video");
      
      // FIX: If the number of images doesn't match, OR if there's a mismatch in videos vs images (like swapping a video for an image), do a HARD swap of the HTML.
      if (oldImgs.length === 0 || oldImgs.length !== newImgs.length || oldVideos.length !== newVideos.length) {
        oldGallery.innerHTML = newGallery.innerHTML;
        oldGallery.className = newGallery.className;
        
        // If we hard-swapped a video in, make sure it plays
        const newlyInjectedVideos = oldGallery.querySelectorAll("video");
        newlyInjectedVideos.forEach(v => {
           if(v.hasAttribute("autoplay")) v.play().catch(()=>{});
        });

      } else {
        // Safe smooth image swap
        oldImgs.forEach((img, i) => {
          if (newImgs[i]) {
            img.src = newImgs[i].src;
            if (img.srcset) img.srcset = newImgs[i].srcset;
          }
        });
        const oldSources = oldGallery.querySelectorAll("source");
        const newSources = newGallery.querySelectorAll("source");
        oldSources.forEach((source, i) => {
          if (newSources[i]) {
            if (source.srcset) source.srcset = newSources[i].srcset;
          }
        });
        
        let videoUpdated = false;
        const oldVideoSources = oldGallery.querySelectorAll("video source");
        const newVideoSources = newGallery.querySelectorAll("video source");
        oldVideoSources.forEach((src, i) => {
          if (newVideoSources[i]) {
            src.src = newVideoSources[i].src;
            videoUpdated = true;
          }
        });
        if (videoUpdated) oldGallery.querySelectorAll("video").forEach((v) => v.load());
      }
    }

    // 6) Components
    const oldSizes = oldCardInner.querySelector(".cp-sizes-wrapper");
    const newSizes = newCardInner.querySelector(".cp-sizes-wrapper");
    if (oldSizes && newSizes) oldSizes.innerHTML = newSizes.innerHTML;

    const oldBadges = oldCardInner.querySelector(".product-badges");
    const newBadges = newCardInner.querySelector(".product-badges");
    if (oldBadges && newBadges) oldBadges.innerHTML = newBadges.innerHTML;

    const oldQuickAddRoot = oldCardInner.querySelector("quick-add-component");
    const newQuickAddRoot = newCardInner.querySelector("quick-add-component");
    if (oldQuickAddRoot && newQuickAddRoot) oldQuickAddRoot.outerHTML = newQuickAddRoot.outerHTML;

    const oldContent = oldCardInner.querySelector(".product-card__content");
    const newContent = newCardInner.querySelector(".product-card__content");
    if (oldContent && newContent) {
      const styles = newContent.getAttribute("style") || "";
      const extractVar = (varName) => {
        const match = styles.match(new RegExp(`${varName}:\\s*([^;]+)`));
        return match ? match[1].trim() : null;
      };
      const qaDisplay = extractVar("--quick-add-display");
      const qaMobileDisplay = extractVar("--quick-add-mobile-display");
      const qaMobileOpacity = extractVar("--quick-add-mobile-opacity");

      if (qaDisplay !== null) oldContent.style.setProperty("--quick-add-display", qaDisplay);
      if (qaMobileDisplay !== null) oldContent.style.setProperty("--quick-add-mobile-display", qaMobileDisplay);
      if (qaMobileOpacity !== null) oldContent.style.setProperty("--quick-add-mobile-opacity", qaMobileOpacity);
    }

    // 7) Swatch active state
    const swatches = oldCardInner.querySelectorAll(".cp-card-swatch, a[data-cp-swatch-link]");
    swatches.forEach((sw) => sw.classList.remove("is-active"));
    if (clickedSwatch) clickedSwatch.classList.add("is-active");

    // 8) Wake web components
    upgradeCustomElements(oldCardRoot);
  }

  async function onSwatchActivate(e) {
    const sw = findSwatchFromEvent(e);
    if (!sw) return;

    // Only handle/swallow events when this swatch is inside a PRODUCT CARD (grids)
    const oldCardInner = sw.closest("product-card");
    if (!oldCardInner) return; // PDP links will work normally

    hardStop(e);

    const handle = sw.getAttribute("data-cp-product-handle");
    if (!handle) return;

    const currentHandle = oldCardInner.getAttribute("data-cp-handle");
    if (currentHandle === handle) return;

    const oldCardRoot = oldCardInner.closest("product-card-link") || oldCardInner;

    oldCardRoot.style.opacity = "0.5";
    oldCardRoot.style.pointerEvents = "none";
    oldCardRoot.style.transition = "opacity 0.2s ease";

    try {
      await swap(handle, oldCardInner, sw);
    } catch (err) {
      console.warn("[CP] swap failed:", err);
    } finally {
      oldCardRoot.style.opacity = "1";
      oldCardRoot.style.pointerEvents = "auto";
    }
  }

  document.addEventListener("pointerdown", onSwatchActivate, true);
  document.addEventListener("click", onSwatchActivate, true);
  document.addEventListener("mousedown", onSwatchActivate, true);
  document.addEventListener("touchstart", onSwatchActivate, true);
})();